import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { MapPin, MessageCircle, Mail, Phone, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await axios.post(`${API}/contact`, formData);
      
      if (response.data.success) {
        toast.success(t('contact.form.success'), {
          description: 'We will get back to you soon!',
          duration: 5000,
        });

        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          message: '',
        });
      }
    } catch (error) {
      console.error('Contact form error:', error);
      
      const errorMessage = error.response?.data?.detail || 'Failed to send message. Please try again.';
      
      toast.error('Error', {
        description: errorMessage,
        duration: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleWhatsApp = () => {
    // Placeholder WhatsApp link
    window.open('https://wa.me/966500000000', '_blank');
  };

  return (
    <div className="pt-24">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-green-50 to-yellow-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="heading-1 mb-6">{t('contact.title')}</h1>
            <p className="heading-3 text-gray-600 mb-4">{t('contact.subtitle')}</p>
            <p className="text-lg text-gray-600">We're here to help you grow your business</p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Information */}
            <div>
              <h2 className="heading-2 mb-8">Get In Touch</h2>

              <div className="space-y-6">
                {/* Location */}
                <Card className="product-card">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div
                        className="p-3 rounded-xl"
                        style={{ background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(245, 158, 11, 0.1) 100%)' }}
                      >
                        <MapPin className="h-6 w-6" style={{ color: '#10B981' }} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg mb-1 text-gray-800">Location</h3>
                        <p className="text-gray-600">{t('contact.location')}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* WhatsApp */}
                <Card className="product-card cursor-pointer" onClick={handleWhatsApp}>
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div
                        className="p-3 rounded-xl"
                        style={{ background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(245, 158, 11, 0.1) 100%)' }}
                      >
                        <MessageCircle className="h-6 w-6" style={{ color: '#10B981' }} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg mb-1 text-gray-800">{t('contact.whatsapp')}</h3>
                        <p className="text-gray-600">Click to chat with us</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Email */}
                <Card className="product-card">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div
                        className="p-3 rounded-xl"
                        style={{ background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(245, 158, 11, 0.1) 100%)' }}
                      >
                        <Mail className="h-6 w-6" style={{ color: '#10B981' }} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg mb-1 text-gray-800">Email</h3>
                        <p className="text-gray-600">info@royabusiness.sa</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Phone */}
                <Card className="product-card">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div
                        className="p-3 rounded-xl"
                        style={{ background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(245, 158, 11, 0.1) 100%)' }}
                      >
                        <Phone className="h-6 w-6" style={{ color: '#10B981' }} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg mb-1 text-gray-800">Phone</h3>
                        <p className="text-gray-600">+966 50 000 0000</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Saudi Riyal Identity */}
              <div className="mt-8 p-6 bg-gradient-to-br from-green-50 to-yellow-50 rounded-2xl">
                <div className="flex items-center gap-3">
                  <img
                    src="https://customer-assets.emergentagent.com/job_a49fc96e-c6d1-4538-8241-3e5dffe114fb/artifacts/1tnawu9j_riyal%20logo.jpeg"
                    alt="Saudi Riyal"
                    className="w-12 h-12"
                  />
                  <div>
                    <div className="font-bold text-lg" style={{ color: '#10B981' }}>Saudi National Identity</div>
                    <div className="text-gray-600 text-sm">Supporting Vision 2030</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <Card className="product-card">
                <CardContent className="p-8">
                  <h2 className="heading-3 mb-6">Send Us a Message</h2>

                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('contact.form.name')}
                      </label>
                      <Input
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        placeholder="Your full name"
                        className="w-full"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('contact.form.email')}
                      </label>
                      <Input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder="your.email@example.com"
                        className="w-full"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('contact.form.phone')}
                      </label>
                      <Input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="+966 5X XXX XXXX"
                        className="w-full"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('contact.form.message')}
                      </label>
                      <Textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        placeholder="Tell us about your business needs..."
                        rows={5}
                        className="w-full"
                      />
                    </div>

                    <Button type="submit" className="btn-primary w-full" disabled={isSubmitting}>
                      {isSubmitting ? (
                        <>
                          <Loader2 className="h-5 w-5 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        t('contact.form.submit')
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section Placeholder */}
      <section className="py-20 bg-gradient-to-br from-green-50 to-yellow-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div
              className="w-full rounded-2xl overflow-hidden"
              style={{
                height: '400px',
                background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <div className="text-center text-white">
                <MapPin className="h-16 w-16 mx-auto mb-4" />
                <div className="text-2xl font-bold mb-2">Jeddah, Saudi Arabia</div>
                <div className="text-white/80">Serving businesses across the Kingdom</div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
